import { Component, inject, OnInit, signal } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize, map, Observable } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { BreadcrumbService, CslBreadcrumbItem } from '@card-software/library';
import { AuthService } from '@card-software/nexus-utilities';
import { ProductionCardDyeingCycle } from '@app/models/dyeing-dashboard/production-card-dyeing-cycle';
import {
  ActualValueEntry,
  ActualValueEntryForm,
  ConsumptionWarehouseLocation,
  CycleRecipeValuesForm,
  PreviewItem,
  ProductionCardDyeingCycleRecipeValues,
  RecipePreviewItem,
  RecipeValuesStatus,
} from '@app/models/dyeing-dashboard/production-card-dyeing-cycle-recipe-values';
import { InventoryTypeRef } from '@app/models/item/enum/InventoryTypeRef';
import { ItemMaster } from '@app/models/item/item-master/item-master';
import { ItemDetail, Recipe } from '@app/models/item/recipe';
import { DyeingCycleConsumptionRoles } from '@app/models/roles/roles-group';
import { ItemMasterProxyService } from '@app/proxies/item/item-master.proxy.service';
import { ProductionCardsProxyService } from '@app/proxies/production/production-card.proxy.service';
import { ProductionCardDyeingCycleProxyService } from '@app/proxies/production/production-card-dyein-cycle.proxy.service';
import { ProductionCardDyeingCycleRecipeValuesProxyService } from '@app/proxies/production/production-card-dyeing-cycle-recipe-values.proxy.service';
import { EnumService } from '@app/services/enum.service';
import { IndividualProductionCardService } from '@app/services/individual-production-card.service';
import { LocaleHelperService } from '@app/services/locale-helper.service';

@Component({
  selector: 'app-cycle-recipe-values',
  templateUrl: './cycle-recipe-values.component.html',
  styleUrls: ['./cycle-recipe-values.component.scss'],
})
export class CycleRecipeValuesComponent implements OnInit {
  cycleId = signal<string>('');
  cycle = signal<ProductionCardDyeingCycle | null>(null);
  availableRecipes = signal<Recipe[]>([]);
  recipeValues = signal<ProductionCardDyeingCycleRecipeValues | null>(null);
  selectedRecipe = signal<Recipe | null>(null);
  recipePreview = signal<Recipe | null>(null);
  productionCardNumber = signal<string>('');
  waterUsageInLiters = signal<number | null>(null);
  waterConfirmed = signal<boolean>(false);

  /** Per-recipe.id enrichment (ItemDescription + CurrentInventory) for the not-yet-associated preview. */
  private recipeItemEnrichments = signal<Map<string, RecipePreviewItem[]>>(new Map());
  consumptionWarehouse = signal<ConsumptionWarehouseLocation | null>(null);
  /** DyeingCycleConsumptionAdmin can associate/save/edit/complete; Viewer is read-only. */
  canEdit = signal<boolean>(false);

  recipeForm!: FormGroup<CycleRecipeValuesForm>;
  isLoading = signal(0);
  isSaving = signal(false);
  isRecipeAssociated = signal(false);

  private readonly auxiliaryRawMaterialsInventoryTypeRef = InventoryTypeRef.AuxiliaryRawMaterials;

  unitOptions: { label: string; value: string }[] = [];

  RecipeValuesStatus = RecipeValuesStatus;

  get actualValuesFormArray(): FormArray<FormGroup<ActualValueEntryForm>> {
    return this.recipeForm.controls.actualValues;
  }

  private breadcrumbService = inject(BreadcrumbService);

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private cycleProxyService: ProductionCardDyeingCycleProxyService,
    private recipeValuesProxyService: ProductionCardDyeingCycleRecipeValuesProxyService,
    private productionCardProxyService: ProductionCardsProxyService,
    private individualProductionCardService: IndividualProductionCardService,
    private itemMasterProxyService: ItemMasterProxyService,
    private localeHelperService: LocaleHelperService,
    private enumService: EnumService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private translateService: TranslateService,
  ) {
    this.recipeForm = this.fb.group<CycleRecipeValuesForm>({
      recipeReference: new FormControl<string | null>(null, Validators.required),
      actualValues: this.fb.array<FormGroup<ActualValueEntryForm>>([]),
    });

    this.unitOptions = Object.entries(this.enumService.getEnum('UnitOfMeasure', 'itemEnums')?.values || {}).map(([, label]) => ({
      label,
      value: label,
    }));
  }

  ngOnInit(): void {
    this.canEdit.set(AuthService.hasRole(DyeingCycleConsumptionRoles.DyeingCycleConsumptionAdmin));

    this.recipeValuesProxyService.getConsumptionWarehouse().subscribe({
      next: (response) => {
        if (response?.data) {
          this.consumptionWarehouse.set(response.data);
        }
      },
      error: () => {
        /* non-critical for page load — the notice simply falls back to the location code */
      },
    });

    const id = this.route.snapshot.paramMap.get('cycleId') || '';
    this.cycleId.set(id);
    if (this.cycleId()) {
      this.loadData();
    }

    // Listen to recipe selection changes for preview
    this.recipeForm.controls.recipeReference.valueChanges.subscribe((recipeId) => {
      if (recipeId && !this.isRecipeAssociated()) {
        const recipe = this.availableRecipes().find((r) => r.id === recipeId);
        this.recipePreview.set(recipe || null);
      } else {
        this.recipePreview.set(null);
      }
    });
  }

  updateLoadingState(isLoading: boolean): void {
    this.isLoading.set(isLoading ? this.isLoading() + 1 : this.isLoading() - 1);
  }

  loadData(): void {
    this.updateLoadingState(true);

    // Load cycle information by ID
    this.cycleProxyService.getById(this.cycleId()).subscribe({
      next: (response) => {
        if (response?.data) {
          const cycleData = response.data;
          this.cycle.set(cycleData);

          // Load production card by ID to get the production card number
          if (cycleData.productionCardReference) {
            this.productionCardProxyService.getProductionCardByID(cycleData.productionCardReference).subscribe({
              next: (pcResponse) => {
                if (pcResponse?.data?.productionCardNumber) {
                  this.productionCardNumber.set(pcResponse.data.productionCardNumber);

                  this.individualProductionCardService.initialize(this.productionCardNumber()).subscribe({
                    next: () => {
                      this.buildBreadcrumb();
                    },
                    error: (error) => {
                      console.error('Failed to initialize production card service:', error);
                      this.buildBreadcrumb();
                    },
                  });
                } else {
                  this.buildBreadcrumb();
                }
              },
              error: (error) => {
                console.error('Failed to load production card:', error);
                this.buildBreadcrumb();
              },
            });
          } else {
            this.buildBreadcrumb();
          }
        }
      },
      error: (error) => {
        console.error('Failed to load cycle:', error);
        this.updateLoadingState(false);
      },
    });

    // Always load available recipes first (needed for liquorRatio in water usage default)
    // then check recipe values — so populateForm() always has the full recipe object
    this.recipeValuesProxyService.getAvailableRecipes(this.cycleId()).subscribe({
      next: (recipesResponse) => {
        if (recipesResponse?.data) {
          this.availableRecipes.set(recipesResponse.data.map((preview) => preview.recipe));

          const enrichmentsByRecipeId = new Map<string, RecipePreviewItem[]>();
          recipesResponse.data.forEach((preview) => {
            if (preview.recipe.id) {
              enrichmentsByRecipeId.set(preview.recipe.id, preview.itemEnrichments);
            }
          });
          this.recipeItemEnrichments.set(enrichmentsByRecipeId);
        }
      },
      error: () => {
        /* cycle may not have ended yet — ignore silently */
      },
      complete: () => {
        this.recipeValuesProxyService
          .getRecipeValues(this.cycleId())
          .pipe(finalize(() => this.updateLoadingState(false)))
          .subscribe((response) => {
            if (response?.data) {
              // Recipe already associated
              this.recipeValues.set(response.data);
              this.isRecipeAssociated.set(true);
              this.populateForm();
            }
            // If no recipe values: availableRecipes already loaded, show selection UI
          });
      },
    });
  }

  buildBreadcrumb(): void {
    const cycleData = this.cycle();
    const workOrder = this.individualProductionCardService.workOrder;

    if (!cycleData) {
      this.breadcrumbService.set([
        { label: 'Planning' },
        { label: 'Breadcrumb_Production_Card', route: '/work-orders-for-production-cards' },
        { label: 'Breadcrumb_Cycle_Recipe_Values' },
      ]);
      return;
    }

    const crumbs: CslBreadcrumbItem[] = [{ label: 'Planning' }];

    if (workOrder) {
      crumbs.push({
        label: workOrder.orderNumber ?? 'Work Order',
        route: ['/production-cards-details'],
        queryParams: { id: workOrder.id, workOrderId: workOrder.id },
      });
    }

    if (this.productionCardNumber()) {
      crumbs.push({
        label: this.productionCardNumber(),
        route: `/production-card/${this.productionCardNumber()}/details`,
      });
    }

    crumbs.push({
      label: cycleData.cycleNumber
        ? `${cycleData.startStationAssetId} - (Cycle #${cycleData.cycleNumber}) - Recipe Values`
        : 'Breadcrumb_Cycle_Recipe_Values',
    });

    this.breadcrumbService.set(crumbs);
  }

  associateRecipe(): void {
    const recipeReference = this.recipeForm.controls.recipeReference.value;
    if (!recipeReference) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Please select a recipe',
      });
      return;
    }

    // Find and save selected recipe
    const recipe = this.availableRecipes().find((r) => r.id === recipeReference);
    if (recipe) {
      this.selectedRecipe.set(recipe);
    }

    this.isSaving.set(true);
    this.recipeValuesProxyService
      .associateRecipe(this.cycleId(), { recipeReference })
      .pipe(finalize(() => this.isSaving.set(false)))
      .subscribe({
        next: (response) => {
          if (response?.data) {
            this.recipeValues.set(response.data);
            this.isRecipeAssociated.set(true);
            this.recipePreview.set(null); // Clear preview after association
            this.populateForm();
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'Recipe associated successfully',
            });
          }
        },
      });
  }

  // ─── Water Usage ────────────────────────────────────────────────────────────

  private round5(value: number): number {
    return Math.round(value * 100000) / 100000;
  }

  calculateKgTela(): number {
    const cycle = this.cycle();
    if (!cycle) return 0;
    const totalLbs = cycle.dyedBundleReferences.reduce((sum, b) => sum + (b.weightInLbs ?? 0), 0);
    return this.round5(totalLbs / 2.2046);
  }

  calculateDefaultWaterUsage(): number {
    const kgTela = this.calculateKgTela();
    const liquorRatio = this.selectedRecipe()?.liquorRatio ?? 0;
    return this.round5(kgTela * liquorRatio);
  }

  initWaterUsage(): void {
    const cycle = this.cycle();
    const saved = cycle?.waterUsageInLiters;
    if (saved != null && saved > 0) {
      this.waterUsageInLiters.set(this.round5(+saved));
      this.waterConfirmed.set(true); // already saved — start confirmed
    } else {
      this.waterUsageInLiters.set(this.calculateDefaultWaterUsage());
      this.waterConfirmed.set(false); // default value — require confirmation
    }
  }

  onWaterUsageBlur(newValue: number): void {
    const previous = this.waterUsageInLiters() ?? 0;
    if (newValue === previous) return;
    this.waterUsageInLiters.set(newValue);
    this.cycleProxyService.updateWaterUsage(this.cycleId(), newValue).subscribe({
      error: (err) => console.error('Failed to update water usage:', err),
    });
  }

  confirmWater(): void {
    this.waterConfirmed.set(true);
  }

  unconfirmWater(): void {
    this.waterConfirmed.set(false);
  }

  // ─── Kg calculations ────────────────────────────────────────────────────────

  // IngredientUnit (Sierra.Item.Models.Engineering.Enums.IngredientUnit): Percent = 0, GramsPerLiter = 1.
  // The preview recipe (RecipeDto, untouched from the ITEM module) serializes this as the raw
  // numeric enum value; the confirmed table's Unit already comes as the string name (backend does
  // itemDetail.IngredientUnit?.ToString()) — handle both shapes here.
  private isGramsPerLiter(unit: string | number): boolean {
    if (typeof unit === 'number') return unit === 1;
    const u = unit.toLowerCase();
    return u === 'gramsperliter' || u === 'g/l' || u === 'g_l';
  }

  private isPercent(unit: string | number): boolean {
    if (typeof unit === 'number') return unit === 0;
    const u = unit.toLowerCase();
    return u === 'percent' || u === '%';
  }

  private calculateKgForWater(
    estimatedQty: number | null | undefined,
    unit: string | number | null | undefined,
    water: number,
  ): number | null {
    if (estimatedQty == null || unit == null) return null;

    if (this.isGramsPerLiter(unit)) {
      if (water === 0) return 0;
      return this.round5((estimatedQty * water) / 1000);
    } else if (this.isPercent(unit)) {
      const kgTela = this.calculateKgTela();
      if (kgTela === 0) return 0;
      return this.round5((estimatedQty / 100) * kgTela);
    }
    return null;
  }

  calculateEstimatedKg(estimatedQty: number | null | undefined, unit: string | number | null | undefined): number | null {
    return this.calculateKgForWater(estimatedQty, unit, this.waterUsageInLiters() ?? 0);
  }

  /**
   * Same formula as calculateEstimatedKg(), but for the not-yet-associated recipe preview: water
   * usage isn't confirmed yet at this stage, so it falls back to the default (kgTela × liquorRatio
   * of the previewed recipe), same as calculateDefaultWaterUsage().
   */
  calculatePreviewEstimatedKg(estimatedQty: number | null | undefined, unit: string | number | null | undefined): number | null {
    const preview = this.recipePreview();
    if (!preview) return null;

    const kgTela = this.calculateKgTela();
    const liquorRatio = preview.liquorRatio ?? 0;
    const defaultWater = this.round5(kgTela * liquorRatio);

    return this.calculateKgForWater(estimatedQty, unit, defaultWater);
  }

  private convertToKg(actualQuantity: number, unit: string | null | undefined): number | null {
    if (unit == null) return null;
    const water = this.waterUsageInLiters() ?? 0;

    if (this.isGramsPerLiter(unit)) {
      if (water === 0) return 0;
      return this.round5((actualQuantity * water) / 1000);
    } else if (this.isPercent(unit)) {
      const kgTela = this.calculateKgTela();
      if (kgTela === 0) return 0;
      return this.round5((actualQuantity / 100) * kgTela);
    }
    return null;
  }

  private convertFromKg(actualKg: number, unit: string | null | undefined): number | null {
    if (unit == null) return null;
    const water = this.waterUsageInLiters() ?? 0;

    if (this.isGramsPerLiter(unit)) {
      if (water === 0) return 0;
      return this.round5((actualKg * 1000) / water);
    } else if (this.isPercent(unit)) {
      const kgTela = this.calculateKgTela();
      if (kgTela === 0) return 0;
      return this.round5((actualKg / kgTela) * 100);
    }
    return null;
  }

  // ─── Form ────────────────────────────────────────────────────────────────────

  populateForm(): void {
    const values = this.recipeValues();
    if (!values) return;

    // Set selectedRecipe from availableRecipes if available
    const recipe = this.availableRecipes().find((r) => r.id === values.recipeReference);
    if (recipe) {
      this.selectedRecipe.set(recipe);
    } else {
      this.selectedRecipe.set({
        id: values.recipeReference,
        recipeNumber: values.recipeNumber,
        liquorRatio: undefined,
        shadeReference: undefined,
      } as Recipe);
    }

    this.initWaterUsage();

    // Clear existing form array
    this.actualValuesFormArray.clear();

    // Populate actual values
    values.actualValues.forEach((entry) => {
      this.actualValuesFormArray.push(this.createActualValueFormGroup(entry));
    });

    // Enable/disable controls based on recipe status and role (AFTER adding controls).
    // DyeingCycleConsumptionViewer is always read-only, regardless of status.
    if (values.status === RecipeValuesStatus.Completed || !this.canEdit()) {
      this.actualValuesFormArray.controls.forEach((ctrl) => ctrl.disable());
    } else {
      this.actualValuesFormArray.controls.forEach((ctrl) => {
        ctrl.enable();
        // Keep actualKg disabled for confirmed lines
        if (ctrl.controls.confirmed.value) {
          ctrl.controls.actualKg.disable();
        }
      });
    }
  }

  createActualValueFormGroup(entry: ActualValueEntry): FormGroup<ActualValueEntryForm> {
    // Prefer consumptionInKg (exact value the operator typed) over recalculating.
    // If neither is present, pre-populate with the estimated kg so the user can confirm or change it.
    let actualKg: number | null = null;
    let confirmed = false;

    if (entry.consumptionInKg != null) {
      actualKg = entry.consumptionInKg;
      confirmed = true; // already saved by the user previously
    } else if (entry.actualQuantity != null) {
      actualKg = this.convertToKg(entry.actualQuantity, entry.unit) ?? null;
      confirmed = true;
    } else {
      // No saved value yet — pre-populate with estimated kg and require confirmation
      actualKg = this.calculateEstimatedKg(entry.estimatedQuantity ?? null, entry.unit ?? null);
    }

    const group = this.fb.group<ActualValueEntryForm>({
      processSnapshotId: new FormControl<string>(entry.processSnapshotId, { nonNullable: true }),
      stepOrder: new FormControl<number | null>(entry.stepOrder ?? null),
      itemCode: new FormControl<string>(entry.itemCode, { nonNullable: true }),
      itemDescription: new FormControl<string | null>(entry.itemDescription ?? null),
      estimatedQuantity: new FormControl<number | null>(entry.estimatedQuantity ?? null),
      actualQuantity: new FormControl<number | null>(entry.actualQuantity ?? null),
      unit: new FormControl<string | null>(entry.unit ?? null),
      notes: new FormControl<string | null>(entry.notes ?? null),
      actualKg: new FormControl<number | null>(actualKg),
      consumptionInKg: new FormControl<number | null>(entry.consumptionInKg ?? null),
      isManual: new FormControl<boolean>(entry.isManual ?? false, { nonNullable: true }),
      confirmed: new FormControl<boolean>(confirmed, { nonNullable: true }),
      currentInventory: new FormControl<number | null>(entry.currentInventory ?? null),
    });

    if (confirmed) {
      group.controls.actualKg.disable();
    }

    return group;
  }

  calculateVariance(estimated: number | null | undefined, actual: number | null | undefined): string {
    if (estimated === undefined || estimated === null || actual === undefined || actual === null) {
      return '--';
    }

    const variance = actual - estimated;
    const variancePercent = estimated !== 0 ? (variance / estimated) * 100 : 0;
    const sign = variance >= 0 ? '+' : '';

    return `${sign}${variance.toFixed(2)} (${sign}${variancePercent.toFixed(1)}%)`;
  }

  getVariancePercent(estimated: number | null | undefined, actual: number | null | undefined): number {
    if (estimated === undefined || estimated === null || actual === undefined || actual === null || estimated === 0) {
      return 0;
    }
    return Math.abs(((actual - estimated) / estimated) * 100);
  }

  hasHighVariance(estimated: number | null | undefined, actual: number | null | undefined): boolean {
    return this.getVariancePercent(estimated, actual) > 20;
  }

  groupByProcess(actualValues: FormGroup<ActualValueEntryForm>[]): Map<string, FormGroup<ActualValueEntryForm>[]> {
    const grouped = new Map<string, FormGroup<ActualValueEntryForm>[]>();

    actualValues.forEach((formGroup) => {
      const processId = formGroup.controls.processSnapshotId.value;
      if (!grouped.has(processId)) {
        grouped.set(processId, []);
      }
      grouped.get(processId)!.push(formGroup);
    });

    return grouped;
  }

  getProcessGroups(): { processId: string; entries: FormGroup<ActualValueEntryForm>[] }[] {
    const nonManual = this.actualValuesFormArray.controls.filter((fg) => !fg.controls.isManual.value);
    const grouped = this.groupByProcess(nonManual);
    return Array.from(grouped.entries()).map(([processId, entries]) => ({
      processId,
      entries,
    }));
  }

  getManualItems(): FormGroup<ActualValueEntryForm>[] {
    return this.actualValuesFormArray.controls.filter((fg) => fg.controls.isManual.value);
  }

  addManualItem(): void {
    const group = this.fb.group<ActualValueEntryForm>({
      processSnapshotId: new FormControl<string>('', { nonNullable: true }),
      stepOrder: new FormControl<number | null>(null),
      itemCode: new FormControl<string>('', { nonNullable: true }),
      itemDescription: new FormControl<string | null>(null),
      estimatedQuantity: new FormControl<number | null>(0),
      actualQuantity: new FormControl<number | null>(null),
      unit: new FormControl<string | null>(null),
      notes: new FormControl<string | null>(null),
      actualKg: new FormControl<number | null>(null),
      consumptionInKg: new FormControl<number | null>(null),
      isManual: new FormControl<boolean>(true, { nonNullable: true }),
      confirmed: new FormControl<boolean>(true, { nonNullable: true }), // manual items have no estimated to confirm against
      currentInventory: new FormControl<number | null>(null),
    });
    this.actualValuesFormArray.push(group);
  }

  removeManualItem(fg: FormGroup<ActualValueEntryForm>): void {
    const idx = this.actualValuesFormArray.controls.indexOf(fg);
    if (idx !== -1) this.actualValuesFormArray.removeAt(idx);
  }

  public fetchItems = (query: string): Observable<(ItemMaster & { displayLabel: string })[]> =>
    this.itemMasterProxyService.search({ name: query, inventoryTypeReference: this.auxiliaryRawMaterialsInventoryTypeRef }).pipe(
      map((response) =>
        (response?.data?.items ?? []).map((item) => ({
          ...item,
          displayLabel: `${item.itemCode} - ${this.localeHelperService.getLocalizedValue(item.itemDisplayName)}`,
        })),
      ),
    );

  onItemSelected(item: ItemMaster & { displayLabel: string }, fg: FormGroup<ActualValueEntryForm>): void {
    fg.controls.itemCode.setValue(item.itemCode);
    fg.controls.itemDescription.setValue(this.localeHelperService.getLocalizedValue(item.itemDisplayName) || null);
    const unitLabel = this.unitOptions.find((_, i) => i === item.unitOfMeasure)?.label ?? null;
    fg.controls.unit.setValue(unitLabel);
  }

  getPreviewProcessGroups(): { processName: string; processId: string; items: PreviewItem[] }[] {
    const preview = this.recipePreview();
    if (!preview?.processSnapshots) return [];

    const enrichments = (preview.id && this.recipeItemEnrichments().get(preview.id)) || [];
    const enrichmentByItemCode = new Map(enrichments.map((e) => [e.itemCode, e]));

    return preview.processSnapshots.map((process, index) => {
      // Items directly under processSnapshot
      const processItems = (process.itemDetails || []).map((item: ItemDetail) => ({
        stepOrder: undefined as number | undefined,
        stepName: undefined as string | undefined,
        itemCode: item.itemCode,
        itemDescription: enrichmentByItemCode.get(item.itemCode)?.itemDescription,
        estimatedQuantity: item.quantity,
        unit: item.ingredientUnit,
        currentInventory: enrichmentByItemCode.get(item.itemCode)?.currentInventory,
      }));

      // Items from steps
      const stepItems = (process.steps || []).flatMap((step) =>
        (step.itemDetails || []).map((item: ItemDetail) => ({
          stepOrder: step.stepOrder,
          stepName: step.stepName,
          itemCode: item.itemCode,
          itemDescription: enrichmentByItemCode.get(item.itemCode)?.itemDescription,
          estimatedQuantity: item.quantity,
          unit: item.ingredientUnit,
          currentInventory: enrichmentByItemCode.get(item.itemCode)?.currentInventory,
        })),
      );

      const items = [...processItems, ...stepItems];

      return {
        processName: process.processName || `Process ${index + 1}`,
        processId: process.id || `process-${index}`,
        items: items,
      };
    });
  }

  /** Localized name of the chemical consumables warehouse, for the persistent consumption notice/toast. */
  getConsumptionWarehouseName(): string {
    const warehouse = this.consumptionWarehouse();
    if (!warehouse) return '';
    return this.localeHelperService.getLocalizedValue(warehouse.name) || warehouse.code;
  }

  getConsumptionNoticeMessage(): string {
    return this.translateService.instant('Recipe_Values_Consumption_Notice', { warehouse: this.getConsumptionWarehouseName() });
  }

  hasProcessesInPreview(): boolean {
    const processGroups = this.getPreviewProcessGroups();
    return processGroups.length > 0 && processGroups.some((pg) => pg.items.length > 0);
  }

  getRecipeLabel(recipe: Recipe): string {
    let label = recipe.recipeNumber || '';
    if (recipe.manufacturerLotNumberKeys && recipe.manufacturerLotNumberKeys.length > 0) {
      const lots = recipe.manufacturerLotNumberKeys
        .map((key) => key.manufacturerLotNumber)
        .filter((lot) => lot) // Remove null/undefined values
        .join(', ');

      if (lots) {
        label += ` | Yarn Lot: [${lots}]`;
      }
    }
    return label;
  }

  saveDraft(): void {
    const values = this.recipeValues();
    if (!values) return;

    this.isSaving.set(true);
    const formValue = this.recipeForm.getRawValue();

    const updatedRecipeValues: ProductionCardDyeingCycleRecipeValues = {
      ...values,
      actualValues: formValue.actualValues.map((entry) => ({
        processSnapshotId: entry.processSnapshotId,
        stepOrder: entry.stepOrder,
        itemCode: entry.itemCode,
        itemDescription: entry.itemDescription ?? undefined,
        estimatedQuantity: entry.estimatedQuantity ?? undefined,
        actualQuantity: entry.isManual
          ? (entry.actualKg ?? undefined)
          : entry.actualKg != null
            ? (this.convertFromKg(entry.actualKg, entry.unit) ?? undefined)
            : undefined,
        unit: entry.unit ?? undefined,
        notes: entry.notes ?? undefined,
        consumptionInKg: entry.actualKg ?? undefined,
        isManual: entry.isManual ?? false,
      })),
    };

    this.recipeValuesProxyService
      .updateRecipeValues(this.cycleId(), updatedRecipeValues)
      .pipe(finalize(() => this.isSaving.set(false)))
      .subscribe({
        next: (response) => {
          if (response?.data) {
            this.recipeValues.set(response.data);
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'Draft saved successfully',
            });
          }
        },
      });
  }

  markAsComplete(): void {
    this.confirmationService.confirm({
      message: this.translateService.instant('Recipe_Values_Confirm_Message'),
      header: this.translateService.instant('Recipe_Values_Confirm_Header'),
      key: 'rv-dialog',
      acceptLabel: this.translateService.instant('Recipe_Values_Confirm_Accept'),
      rejectLabel: this.translateService.instant('Recipe_Values_Confirm_Reject'),
      accept: () => this.executeMarkAsComplete(),
    });
  }

  private executeMarkAsComplete(): void {
    // Before completing, save the draft to persist actualQuantity conversions
    const values = this.recipeValues();
    if (!values) return;

    this.isSaving.set(true);
    const formValue = this.recipeForm.getRawValue();

    const updatedRecipeValues: ProductionCardDyeingCycleRecipeValues = {
      ...values,
      actualValues: formValue.actualValues.map((entry) => ({
        processSnapshotId: entry.processSnapshotId,
        stepOrder: entry.stepOrder,
        itemCode: entry.itemCode,
        itemDescription: entry.itemDescription ?? undefined,
        estimatedQuantity: entry.estimatedQuantity ?? undefined,
        actualQuantity: entry.isManual
          ? (entry.actualKg ?? undefined)
          : entry.actualKg != null
            ? (this.convertFromKg(entry.actualKg, entry.unit) ?? undefined)
            : undefined,
        unit: entry.unit ?? undefined,
        notes: entry.notes ?? undefined,
        consumptionInKg: entry.actualKg ?? undefined,
        isManual: entry.isManual ?? false,
      })),
    };

    this.recipeValuesProxyService.updateRecipeValues(this.cycleId(), updatedRecipeValues).subscribe({
      next: () => {
        this.recipeValuesProxyService
          .completeRecipeValues(this.cycleId())
          .pipe(finalize(() => this.isSaving.set(false)))
          .subscribe({
            next: (response) => {
              if (response?.data) {
                this.recipeValues.set(response.data);
                this.actualValuesFormArray.controls.forEach((ctrl) => ctrl.disable());
                this.messageService.add({
                  severity: 'success',
                  summary: 'Success',
                  detail: 'Recipe values marked as complete',
                });
              }
            },
          });
      },
      error: () => this.isSaving.set(false),
    });
  }

  confirmLine(fg: FormGroup<ActualValueEntryForm>): void {
    fg.controls.confirmed.setValue(true);
    fg.controls.actualKg.disable();
  }

  unconfirmLine(fg: FormGroup<ActualValueEntryForm>): void {
    fg.controls.confirmed.setValue(false);
    fg.controls.actualKg.enable();
  }

  allRecipeLinesConfirmed(): boolean {
    return this.getProcessGroups()
      .flatMap((g) => g.entries)
      .every((fg) => fg.controls.confirmed.value);
  }

  /**
   * Items whose total Actual (Kg) across all recipe lines exceeds the on-hand balance at
   * NT.WH-LD-1. Purely informational — Inventory.API still enforces this as a hard business rule
   * on Mark as Complete (rejects the consumption transaction), this just surfaces it beforehand.
   */
  getInsufficientInventoryItems(): { itemCode: string; itemDescription: string; required: number; available: number }[] {
    const totals = new Map<string, { itemDescription: string; required: number; available: number }>();

    this.actualValuesFormArray.controls.forEach((fg) => {
      const itemCode = fg.controls.itemCode.value;
      if (!itemCode) return;

      const required = fg.controls.actualKg.value ?? 0;
      const available = fg.controls.currentInventory.value ?? 0;
      const itemDescription = fg.controls.itemDescription.value || itemCode;

      const existing = totals.get(itemCode);
      if (existing) {
        existing.required += required;
      } else {
        totals.set(itemCode, { itemDescription, required, available });
      }
    });

    return Array.from(totals.entries())
      .map(([itemCode, v]) => ({ itemCode, ...v }))
      .filter((v) => v.required - v.available > 0.00001)
      .sort((a, b) => b.required - b.available - (a.required - a.available));
  }

  hasInsufficientInventory(): boolean {
    return this.getInsufficientInventoryItems().length > 0;
  }

  goBack(): void {
    if (this.productionCardNumber()) {
      this.router.navigate(['/production-card', this.productionCardNumber(), 'details']);
    }
  }
}
